import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    FileOutput,
    Pencil,
    Link,
    Folder,
    Magnet,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { EVENT_KEYS, trackClick } from '../../../services/api/trackingService';
import LoadingRing from '../../ui-components/LoadingRing';
import {
    PolicyDocumentView,
    PolicyDocumentMeta,
} from '../read/PolicyDocumentView';
import { PolicyPreviewDialog } from '../read/PolicyPreviewDialog';
import { PolicyEditLog } from '../read/PolicyEditLog';
import { DocumentSchema } from '../../documents/types';
import {
    getPolicy,
    getLinkedImplants,
    getGroupsForPolicy,
    getRoomsForPolicy,
    PopulatedPolicy,
} from '../../../services/api/policyService';
import PolicyApprovalActions from '../approval/PolicyApprovalActions';
import { getDocumentSchema } from '../../../services/documentSchemaService';
import { resolveVariables } from '../../../services/api/departmentVariablesService';
import { interpolateVariables } from '../utils/interpolateVariables';
import AuthContext from '../../../store/auth-context';
import PERMISSION_KEYS from '../../../constants/permission-keys';
import RIGHTS_KEYS from '../../../constants/rights-keys';
import { mergePersistedConditionalsIntoDocumentData } from '../conditionals/conditionalsUtils';
import LinkedImplantsDialog from './LinkedImplantsDialog';
import PolicyFoldersDialog from './PolicyFoldersDialog';
import PolicyRoomsDialog from './PolicyRoomsDialog';

export default function PolicyReadPage() {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const context = useContext(AuthContext);
    const isAdmin = context.hasPermission(PERMISSION_KEYS.managePolicies);
    const canManageFolders =
        isAdmin && context.hasPermission(PERMISSION_KEYS.manageGroups);
    const canApprove = context.hasPermission(PERMISSION_KEYS.approvePolicy);

    const [policy, setPolicy] = useState<PopulatedPolicy | null>(null);
    const [schema, setSchema] = useState<DocumentSchema | null>(null);
    const [docData, setDocData] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [implantsOpen, setImplantsOpen] = useState(false);
    const [foldersOpen, setFoldersOpen] = useState(false);
    const [roomsOpen, setRoomsOpen] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    const [implantCount, setImplantCount] = useState<number | null>(null);
    const [folderCount, setFolderCount] = useState<number | null>(null);
    const [roomCount, setRoomCount] = useState<number | null>(null);

    useEffect(() => {
        if (!id) return;
        const load = async () => {
            try {
                const policyData = await getPolicy(id);
                const doc = policyData.document_id;

                if (!doc || !doc.schemaKey) {
                    setError(t('departmentPolicies.readPage.noSchemaError'));
                    return;
                }

                const [schemaRecord, varMap] = await Promise.all([
                    getDocumentSchema(doc.schemaKey, doc.schemaVersion),
                    resolveVariables().catch(() => ({})),
                ]);

                const interpolated =
                    Object.keys(varMap).length > 0
                        ? interpolateVariables(doc.data, varMap)
                        : doc.data;

                setPolicy(policyData);
                setSchema(schemaRecord.schema);
                setDocData(
                    mergePersistedConditionalsIntoDocumentData(
                        interpolated,
                        policyData.conditionals
                    )
                );

                const loadImplantCount = async () => {
                    try {
                        const data = await getLinkedImplants(id);
                        setImplantCount(data.length);
                    } catch {}
                };
                const loadFolderCount = async () => {
                    try {
                        const data = await getGroupsForPolicy(id);
                        setFolderCount(data.length);
                    } catch {}
                };
                const loadRoomCount = async () => {
                    try {
                        const rooms = await getRoomsForPolicy(id);
                        setRoomCount(
                            rooms.reduce(
                                (sum, room) =>
                                    sum +
                                    (room.linkedOnRoom ? 1 : 0) +
                                    room.equipment.length,
                                0
                            )
                        );
                    } catch {}
                };
                loadImplantCount();
                loadFolderCount();
                loadRoomCount();
            } catch (err) {
                console.error('Failed to load policy:', err);
                setError(t('departmentPolicies.readPage.failedToLoad'));
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    if (loading) {
        return (
            <div className="flex justify-center py-32">
                <LoadingRing size="large" />
            </div>
        );
    }

    const doc = policy?.document_id;
    if (error || !policy || !schema || !doc) {
        return (
            <div className="container mx-auto max-w-3xl px-4 py-8">
                <p className="text-sm text-destructive">
                    {error ?? t('departmentPolicies.readPage.policyNotFound')}
                </p>
            </div>
        );
    }

    const meta: PolicyDocumentMeta = {
        title: doc.title,
        status: doc.status,
        effectiveDate: policy.effectiveDate,
        expiryDate: policy.expiryDate,
        tags: policy.tags ?? [],
        authors: (policy.coAuthors ?? []).map(
            (c: any) => c.name || t('departmentPolicies.readPage.unknown')
        ),
        attachments: policy.attachments ?? [],
    };

    const hasEditLog = policy.editLog && policy.editLog.length > 0;

    return (
        <div className="px-4 py-8 sm:px-6">
            <div className="max-w-5xl mx-auto">
                <div className="mb-4 max-w-3xl">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/department-policies')}
                    >
                        <ArrowLeft className="mr-1 h-4 w-4" />
                        {t('departmentPolicies.readPage.policies')}
                    </Button>
                </div>
                <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
                    {/* Side panel */}
                    <div className="order-1 w-full shrink-0 lg:order-2 lg:w-48">
                        <div className="space-y-4 lg:sticky lg:top-8">
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                                {isAdmin && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full justify-center whitespace-normal h-auto min-h-9 py-1.5 text-center"
                                        onClick={() =>
                                            navigate(
                                                `/department-policies/${id}/edit`
                                            )
                                        }
                                    >
                                        <Pencil className="h-3.5 w-3.5 mr-2" />
                                        {t('departmentPolicies.readPage.edit')}
                                    </Button>
                                )}

                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full justify-center whitespace-normal h-auto min-h-9 py-1.5 text-center"
                                    onClick={() => {
                                        trackClick(EVENT_KEYS.clickExport, {
                                            previewSource: 'policy',
                                        });
                                        setExportOpen(true);
                                    }}
                                >
                                    <FileOutput className="h-3.5 w-3.5 mr-2" />
                                    {t(
                                        'departmentPolicies.readPage.export',
                                        'Export'
                                    )}
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full justify-center whitespace-normal h-auto min-h-9 py-1.5 text-center"
                                    onClick={() => setImplantsOpen(true)}
                                >
                                    <Link className="h-3.5 w-3.5 mr-2" />
                                    {t(
                                        'departmentPolicies.readPage.linkedImplants'
                                    )}
                                    {implantCount !== null &&
                                        implantCount > 0 && (
                                            <span className="ml-1.5 rounded-full bg-primary/15 px-1.5 text-xs text-primary">
                                                {implantCount}
                                            </span>
                                        )}
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full justify-center whitespace-normal h-auto min-h-9 py-1.5 text-center"
                                    onClick={() => setFoldersOpen(true)}
                                >
                                    <Folder className="h-3.5 w-3.5 mr-2" />
                                    {t('departmentPolicies.readPage.folders')}
                                    {folderCount !== null &&
                                        folderCount > 0 && (
                                            <span className="ml-1.5 rounded-full bg-primary/15 px-1.5 text-xs text-primary">
                                                {folderCount}
                                            </span>
                                        )}
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full justify-center whitespace-normal h-auto min-h-9 py-1.5 text-center"
                                    onClick={() => setRoomsOpen(true)}
                                >
                                    <Magnet className="h-3.5 w-3.5 mr-2" />
                                    {t('departmentPolicies.readPage.rooms')}
                                    {roomCount !== null && roomCount > 0 && (
                                        <span className="ml-1.5 rounded-full bg-primary/15 px-1.5 text-xs text-primary">
                                            {roomCount}
                                        </span>
                                    )}
                                </Button>
                            </div>

                            <PolicyApprovalActions
                                policy={policy}
                                setPolicy={setPolicy}
                                canApprove={canApprove}
                            />

                            {hasEditLog && (
                                <>
                                    <div className="border-t border-border/20" />
                                    <PolicyEditLog
                                        editLog={policy.editLog}
                                        hidePreviewOnMobile
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    <div className="order-2 flex-1 min-w-0 max-w-3xl lg:order-1">
                        <div className="rounded-lg border border-subtle bg-zinc-800/70 p-6 md:p-8">
                            <PolicyDocumentView
                                schema={schema}
                                data={docData}
                                meta={meta}
                                hideToolbar
                            />
                        </div>
                    </div>
                </div>

                {id && (
                    <>
                        <LinkedImplantsDialog
                            policyId={id}
                            open={implantsOpen}
                            onOpenChange={setImplantsOpen}
                            canManage={isAdmin}
                            onCountChange={setImplantCount}
                        />
                        <PolicyFoldersDialog
                            policyId={id}
                            open={foldersOpen}
                            onOpenChange={setFoldersOpen}
                            canManage={canManageFolders}
                            onCountChange={setFolderCount}
                        />
                        <PolicyRoomsDialog
                            policyId={id}
                            open={roomsOpen}
                            onOpenChange={setRoomsOpen}
                            canManage={isAdmin}
                            onCountChange={setRoomCount}
                        />
                        {exportOpen && (
                            <PolicyPreviewDialog
                                open={exportOpen}
                                onOpenChange={setExportOpen}
                                schema={schema}
                                data={docData}
                                meta={meta}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
